import { processImageForUpload } from './imageUtils'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AdminNewsArticle {
  id: string
  slug: string
  date: string
  category: string
  title: string
  text: string
  image_url: string | null
  content: string
  is_featured: boolean
  is_draft: boolean
  group_slug?: string | null
  seo_title: string | null
  seo_description: string | null
  seo_no_index: boolean
  seo_focus_keyword: string | null
  og_title: string | null
  og_description: string | null
  og_image_url: string | null
  created_at: string
  updated_at: string
}

export interface AdminMarqueeItem {
  id: string
  date: string
  text: string
  created_at: string
}

export interface AdminGroup {
  id: string
  slug: string
  name: string
  tagline: string
  description: string
  debut_date: string
  color_primary: string
  cover_image_url: string
  logo_url: string | null
  social_handles: Record<string, string>
  is_active: boolean
  created_at: string
}

export interface AdminMember {
  id: string
  group_id: string
  group_slug: string
  stage_name: string
  real_name: string | null
  role: string
  positions: string[]
  image_url: string
  birth_year: number | null
  debut_date: string | null
  is_active: boolean
  display_order: number
  created_at: string
}

export interface AdminRelease {
  id: string
  group_slug: string
  image_url: string
  alt: string
  spotify_url: string
  label: string
  title: string
  release_date: string
  tracks: string[]
  more_tracks: number | null
  created_at: string
}

export interface AdminScheduleEvent {
  id: string
  group_slug: string
  name: string
  date: string | null
  location: string
  time: string
  badge: string
  cheki_url: string | null
  created_at: string
}

export interface AdminMediaItem {
  id: string
  group_slug: string | null
  image_url: string
  alt: string
  badge: string
  duration: string
  date: string
  title: string
  is_featured: boolean
  youtube_url: string | null
  created_at: string
}

// ─── Fetch helper ─────────────────────────────────────────────────────────────

const API_BASE_URL = import.meta.env.VITE_API_URL ?? ''

async function authFetch<T>(
  token: string,
  path: string,
  options?: RequestInit,
): Promise<T> {
  const res = await fetch(`${API_BASE_URL}/api/v1/admin${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options?.headers ?? {}),
    },
  })
  const json = await res.json() as { success: boolean; data: T; error?: { message: string } }
  if (!res.ok) throw new Error(json.error?.message ?? `HTTP ${res.status}`)
  return json.data
}

// ─── News ─────────────────────────────────────────────────────────────────────

export const getAdminNews = (token: string) =>
  authFetch<AdminNewsArticle[]>(token, '/news')

export const createNews = (token: string, data: Omit<AdminNewsArticle, 'id' | 'created_at' | 'updated_at'>) =>
  authFetch<AdminNewsArticle>(token, '/news', { method: 'POST', body: JSON.stringify(data) })

export const updateNews = (token: string, id: string, data: Partial<Omit<AdminNewsArticle, 'id' | 'created_at'>>) =>
  authFetch<AdminNewsArticle>(token, `/news/${id}`, { method: 'PUT', body: JSON.stringify(data) })

export const deleteNews = (token: string, id: string) =>
  authFetch<null>(token, `/news/${id}`, { method: 'DELETE' })

// ─── Marquee ──────────────────────────────────────────────────────────────────

export const getAdminMarquee = (token: string) =>
  authFetch<AdminMarqueeItem[]>(token, '/marquee')

export const createMarquee = (token: string, data: { date: string; text: string }) =>
  authFetch<AdminMarqueeItem>(token, '/marquee', { method: 'POST', body: JSON.stringify(data) })

export const deleteMarquee = (token: string, id: string) =>
  authFetch<null>(token, `/marquee/${id}`, { method: 'DELETE' })

// ─── Groups ───────────────────────────────────────────────────────────────────

export const getAdminGroups = (token: string) =>
  authFetch<AdminGroup[]>(token, '/groups')

export const createGroup = (token: string, data: Omit<AdminGroup, 'created_at'>) =>
  authFetch<AdminGroup>(token, '/groups', { method: 'POST', body: JSON.stringify(data) })

export const updateGroup = (token: string, id: string, data: Partial<Omit<AdminGroup, 'id' | 'slug' | 'created_at'>>) =>
  authFetch<AdminGroup>(token, `/groups/${id}`, { method: 'PUT', body: JSON.stringify(data) })

export const deleteGroup = (token: string, id: string) =>
  authFetch<null>(token, `/groups/${id}`, { method: 'DELETE' })

// ─── Members ──────────────────────────────────────────────────────────────────

export const getAdminMembers = (token: string) =>
  authFetch<AdminMember[]>(token, '/members')

export const createMember = (token: string, data: Omit<AdminMember, 'created_at'>) =>
  authFetch<AdminMember>(token, '/members', { method: 'POST', body: JSON.stringify(data) })

export const updateMember = (token: string, id: string, data: Partial<Omit<AdminMember, 'id' | 'created_at'>>) =>
  authFetch<AdminMember>(token, `/members/${id}`, { method: 'PUT', body: JSON.stringify(data) })

export const deleteMember = (token: string, id: string) =>
  authFetch<null>(token, `/members/${id}`, { method: 'DELETE' })

export const patchMembersOrder = (
  token: string,
  groupId: string,
  orderedIds: string[],
) =>
  authFetch<null>(token, '/members/reorder', {
    method: 'PATCH',
    body: JSON.stringify({ group_id: groupId, ordered_ids: orderedIds }),
  })

// ─── Discography ──────────────────────────────────────────────────────────────

export const getAdminDiscography = (token: string) =>
  authFetch<AdminRelease[]>(token, '/discography')

export const createDiscography = (token: string, data: Omit<AdminRelease, 'id' | 'created_at'>) =>
  authFetch<AdminRelease>(token, '/discography', { method: 'POST', body: JSON.stringify(data) })

export const updateDiscography = (token: string, id: string, data: Partial<Omit<AdminRelease, 'id' | 'created_at'>>) =>
  authFetch<AdminRelease>(token, `/discography/${id}`, { method: 'PUT', body: JSON.stringify(data) })

export const deleteDiscography = (token: string, id: string) =>
  authFetch<null>(token, `/discography/${id}`, { method: 'DELETE' })

// ─── Schedule ─────────────────────────────────────────────────────────────────

export const getAdminSchedule = (token: string) =>
  authFetch<AdminScheduleEvent[]>(token, '/schedule')

export const createSchedule = (token: string, data: Omit<AdminScheduleEvent, 'id' | 'created_at'>) =>
  authFetch<AdminScheduleEvent>(token, '/schedule', { method: 'POST', body: JSON.stringify(data) })

export const updateSchedule = (token: string, id: string, data: Partial<Omit<AdminScheduleEvent, 'id' | 'created_at'>>) =>
  authFetch<AdminScheduleEvent>(token, `/schedule/${id}`, { method: 'PUT', body: JSON.stringify(data) })

export const deleteSchedule = (token: string, id: string) =>
  authFetch<null>(token, `/schedule/${id}`, { method: 'DELETE' })

// ─── Media ────────────────────────────────────────────────────────────────────

export const getAdminMedia = (token: string) =>
  authFetch<AdminMediaItem[]>(token, '/media')

export const createMedia = (token: string, data: Omit<AdminMediaItem, 'id' | 'created_at'>) =>
  authFetch<AdminMediaItem>(token, '/media', { method: 'POST', body: JSON.stringify(data) })

export const updateMedia = (token: string, id: string, data: Partial<Omit<AdminMediaItem, 'id' | 'created_at'>>) =>
  authFetch<AdminMediaItem>(token, `/media/${id}`, { method: 'PUT', body: JSON.stringify(data) })

export const deleteMedia = (token: string, id: string) =>
  authFetch<null>(token, `/media/${id}`, { method: 'DELETE' })

// ─── Assets ───────────────────────────────────────────────────────────────────

export interface AdminAsset {
  id: string
  url: string
  filename: string
  alt: string
  mime_type: string
  size: number
  imagekit_file_id: string
  folder_id: string | null
  created_at: string
}

export interface AssetFolder {
  id: string
  name: string
  created_at: string
}

export interface AssetUsageReference {
  entity_type: string
  entity_id: string
  entity_name: string
  column: string
}

export interface AssetUsageResult {
  count: number
  references: AssetUsageReference[]
}

export const getFolders = (token: string) =>
  authFetch<AssetFolder[]>(token, '/asset-folders')

export const createFolder = (token: string, name: string) =>
  authFetch<AssetFolder>(token, '/asset-folders', {
    method: 'POST',
    body: JSON.stringify({ name }),
  })

export const renameFolder = (token: string, id: string, name: string) =>
  authFetch<AssetFolder>(token, `/asset-folders/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  })

export const deleteFolder = (token: string, id: string) =>
  authFetch<null>(token, `/asset-folders/${id}`, { method: 'DELETE' })

export const bulkMoveAssets = (
  token: string,
  assetIds: string[],
  folderId: string | null,
) =>
  authFetch<null>(token, '/assets/bulk-move', {
    method: 'PATCH',
    body: JSON.stringify({ asset_ids: assetIds, folder_id: folderId }),
  })

export const getAssets = (token: string) =>
  authFetch<AdminAsset[]>(token, '/assets')

export const updateAsset = (
  token: string,
  id: string,
  data: { filename?: string; alt?: string; folder_id?: string | null },
) => authFetch<AdminAsset>(token, `/assets/${id}`, { method: 'PATCH', body: JSON.stringify(data) })

export const deleteAsset = (token: string, id: string) =>
  authFetch<null>(token, `/assets/${id}`, { method: 'DELETE' })

export const getAssetUsage = (token: string, assetId: string) =>
  authFetch<AssetUsageResult>(token, `/assets/${assetId}/usage`)

export async function deleteAssetSafe(
  token: string,
  id: string,
): Promise<{ ok: true } | { ok: false; inUse: boolean; message: string; references?: AssetUsageReference[] }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/admin/assets/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })
  if (res.ok) return { ok: true }
  const json = await res.json() as { success: boolean; error?: { code: string; message: string; references?: AssetUsageReference[] } }
  return {
    ok: false,
    inUse: json.error?.code === 'ASSET_IN_USE',
    message: json.error?.message ?? `HTTP ${res.status}`,
    references: json.error?.references,
  }
}

export async function deleteFolderSafe(
  token: string,
  id: string,
): Promise<{ ok: true } | { ok: false; notEmpty: boolean; message: string }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/admin/asset-folders/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })
  if (res.ok) return { ok: true }
  const json = await res.json() as { success: boolean; error?: { code: string; message: string } }
  return {
    ok: false,
    notEmpty: json.error?.code === 'FOLDER_NOT_EMPTY',
    message: json.error?.message ?? `HTTP ${res.status}`,
  }
}

export async function uploadAsset(
  token: string,
  file: File,
  options?: { filename?: string; alt?: string; folder_id?: string | null; feature?: string },
  onProgress?: (progress: number) => void,
): Promise<AdminAsset> {
  const processedFile = await processImageForUpload(file)
  const form = new FormData()
  form.append('file', processedFile)
  if (options?.filename) form.append('filename', options.filename)
  if (options?.alt !== undefined) form.append('alt', options.alt)
  if (options?.folder_id) form.append('folder_id', options.folder_id)
  if (options?.feature) form.append('feature', options.feature)

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 100))
    }
    xhr.onload = () => {
      try {
        const json = JSON.parse(xhr.responseText) as { success: boolean; data: AdminAsset; error?: { message: string } }
        if (xhr.status >= 200 && xhr.status < 300) resolve(json.data)
        else reject(new Error(json.error?.message ?? 'Upload failed'))
      } catch {
        reject(new Error('Upload failed'))
      }
    }
    xhr.onerror = () => reject(new Error('Upload failed'))
    xhr.open('POST', `${API_BASE_URL}/api/v1/admin/assets`)
    xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.send(form)
  })
}

// ─── Sale Events ─────────────────────────────────────────────────────────────

export interface AdminSaleEvent {
  id: string
  title: string
  description: string
  date: string
  time: string | null
  location: string
  banner_image_url: string | null
  is_published: boolean
  terms: string | null
  created_at: string
  updated_at: string
}

export interface AdminSaleEventPhase {
  id: string
  sale_event_id: string
  name: string
  price: number
  stock: number
  is_open: boolean
  display_order: number
  created_at: string
  stock_sold?: number
  stock_remaining?: number
}

export interface AppSetting {
  key: string
  value: string
  updated_at: string
}

export interface AdminSaleOrder {
  id: string
  order_code: string
  sale_event_id: string
  buyer_name: string
  buyer_phone: string
  buyer_email: string
  quantity: number
  notes: string | null
  proof_image_url: string
  status: 'pending' | 'confirmed' | 'rejected'
  confirmed_at: string | null
  created_at: string
}

export interface AdminSaleOrderWithEvent extends AdminSaleOrder {
  sale_events: {
    title: string
    date: string
    time: string | null
    location: string
  }
  sale_event_phases: { name: string; price: number } | null
}

export const getAdminSaleEvents = (token: string) =>
  authFetch<AdminSaleEvent[]>(token, '/events')

export const getAdminSaleEvent = (token: string, id: string) =>
  authFetch<AdminSaleEvent>(token, `/events/${id}`)

export const createSaleEvent = (
  token: string,
  data: Omit<AdminSaleEvent, 'id' | 'created_at' | 'updated_at'>
) => authFetch<AdminSaleEvent>(token, '/events', { method: 'POST', body: JSON.stringify(data) })

export const updateSaleEvent = (
  token: string,
  id: string,
  data: Partial<Omit<AdminSaleEvent, 'id' | 'created_at' | 'updated_at'>>
) => authFetch<AdminSaleEvent>(token, `/events/${id}`, { method: 'PUT', body: JSON.stringify(data) })

export const deleteSaleEvent = (token: string, id: string) =>
  authFetch<null>(token, `/events/${id}`, { method: 'DELETE' })

export interface AdminOrderTicket {
  id: string
  ticket_code: string
  ticket_index: number
  is_checked_in: boolean
  checked_in_at: string | null
  checked_in_by: string | null
}

export type AdminSaleOrderWithPhase = AdminSaleOrder & {
  sale_event_phases: { name: string; price: number } | null
  tickets: AdminOrderTicket[]
}

export const getAdminSaleOrders = (token: string, eventId: string) =>
  authFetch<AdminSaleOrderWithPhase[]>(token, `/events/${eventId}/orders`)

export const getAdminSaleOrder = (token: string, orderId: string) =>
  authFetch<AdminSaleOrderWithEvent>(token, `/events/orders/${orderId}`)

export const updateOrderStatus = (
  token: string,
  orderId: string,
  status: 'confirmed' | 'rejected'
) =>
  authFetch<AdminSaleOrder>(token, `/events/orders/${orderId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  })

// ─── Sale Event Phases ────────────────────────────────────────────────────────

export const getAdminPhases = (token: string, eventId: string) =>
  authFetch<AdminSaleEventPhase[]>(token, `/events/${eventId}/phases`)

export const createPhase = (token: string, eventId: string, data: {
  name: string; price: number; stock: number; display_order: number
}) => authFetch<AdminSaleEventPhase>(token, `/events/${eventId}/phases`, {
  method: 'POST', body: JSON.stringify(data),
})

export const updatePhase = (token: string, eventId: string, phaseId: string, data: Partial<{
  name: string; price: number; stock: number; display_order: number
}>) => authFetch<AdminSaleEventPhase>(token, `/events/${eventId}/phases/${phaseId}`, {
  method: 'PUT', body: JSON.stringify(data),
})

export const deletePhase = (token: string, eventId: string, phaseId: string) =>
  authFetch<null>(token, `/events/${eventId}/phases/${phaseId}`, { method: 'DELETE' })

export const openPhase = (token: string, eventId: string, phaseId: string) =>
  authFetch<AdminSaleEventPhase>(token, `/events/${eventId}/phases/${phaseId}/open`, { method: 'PATCH' })

export const closePhase = (token: string, eventId: string, phaseId: string) =>
  authFetch<AdminSaleEventPhase>(token, `/events/${eventId}/phases/${phaseId}/close`, { method: 'PATCH' })

// ─── Event Check-ins ─────────────────────────────────────────────────────────

export interface EventCheckin {
  id: string
  sale_event_id: string
  order_id: string
  checked_in_at: string
  checked_in_by: string | null
  created_at: string
  sale_orders: {
    order_code: string
    buyer_name: string
    buyer_phone: string
    quantity: number
    sale_event_phases: { name: string } | null
  }
}

export const getEventCheckins = (token: string, eventId: string) =>
  authFetch<EventCheckin[]>(token, `/events/${eventId}/checkins`)

export const checkInOrder = (token: string, eventId: string, orderId: string) =>
  authFetch<EventCheckin>(token, `/events/${eventId}/checkins`, {
    method: 'POST',
    body: JSON.stringify({ order_id: orderId }),
  })

export const checkInTicket = (token: string, eventId: string, ticketCode: string) =>
  authFetch<{ id: string; ticket_index: number; checked_in_at: string; checked_in_by: string | null }>(
    token,
    `/events/${eventId}/checkins`,
    { method: 'POST', body: JSON.stringify({ ticket_code: ticketCode }) }
  )

// ─── Checkin Sessions ─────────────────────────────────────────────────────────

export interface CheckinSession {
  id: string
  sale_event_id: string
  operator_name: string
  token: string
  is_active: boolean
  created_at: string
}

export const getCheckinSessions = (token: string, eventId: string) =>
  authFetch<CheckinSession[]>(token, `/events/${eventId}/checkin-sessions`)

export const createCheckinSession = (token: string, eventId: string, operator_name: string) =>
  authFetch<CheckinSession>(token, `/events/${eventId}/checkin-sessions`, {
    method: 'POST',
    body: JSON.stringify({ operator_name }),
  })

export const revokeCheckinSession = (token: string, sessionId: string) =>
  authFetch<null>(token, `/events/checkin-sessions/${sessionId}`, { method: 'DELETE' })

// ─── Settings ────────────────────────────────────────────────────────────────

export const getAdminSettings = (token: string) =>
  authFetch<AppSetting[]>(token, '/settings')

export const updateAdminSetting = (token: string, key: string, value: string) =>
  authFetch<AppSetting>(token, `/settings/${key}`, {
    method: 'PUT',
    body: JSON.stringify({ value }),
  })

// ─── Upload (deprecated — gunakan uploadAsset) ────────────────────────────────

export async function uploadImage(token: string, file: File): Promise<string> {
  const asset = await uploadAsset(token, file)
  return asset.url
}

// ─── Organizations / Tenants ──────────────────────────────────────────────────

export interface AdminOrganization {
  id: string
  name: string
  slug: string
  logo_url: string | null
  blast_email_enabled: boolean
  created_at: string
}

export interface AdminOrgUser {
  user_id: string
  email: string
  role: 'manager' | 'operator'
  is_disabled: boolean
}

export const getAdminOrganizations = (token: string) =>
  authFetch<AdminOrganization[]>(token, '/organizations')

export const getAdminOrganization = (token: string, id: string) =>
  authFetch<AdminOrganization>(token, `/organizations/${id}`)

export const createOrganization = (
  token: string,
  data: { name: string; slug: string; logo_url?: string | null }
) => authFetch<AdminOrganization>(token, '/organizations', { method: 'POST', body: JSON.stringify(data) })

export const updateOrganization = (
  token: string,
  id: string,
  data: { name?: string; slug?: string; logo_url?: string | null; blast_email_enabled?: boolean }
) => authFetch<AdminOrganization>(token, `/organizations/${id}`, { method: 'PATCH', body: JSON.stringify(data) })

export const deleteOrganization = (token: string, id: string) =>
  authFetch<null>(token, `/organizations/${id}`, { method: 'DELETE' })

export const getOrgUsers = (token: string, orgId: string) =>
  authFetch<AdminOrgUser[]>(token, `/organizations/${orgId}/users`)

export const createOrgUser = (
  token: string,
  orgId: string,
  data: { email: string; password: string; role: 'manager' | 'operator' }
) => authFetch<{ user_id: string; email: string; role: string }>(token, `/organizations/${orgId}/users`, {
  method: 'POST',
  body: JSON.stringify(data),
})

export const changeOrgUserRole = (
  token: string,
  orgId: string,
  uid: string,
  role: 'manager' | 'operator'
) => authFetch<{ user_id: string; role: string }>(token, `/organizations/${orgId}/users/${uid}/role`, {
  method: 'PATCH',
  body: JSON.stringify({ role }),
})

export const toggleOrgUserDisable = (
  token: string,
  orgId: string,
  uid: string,
  disable: boolean
) => authFetch<null>(token, `/organizations/${orgId}/users/${uid}/disable`, {
  method: 'PATCH',
  body: JSON.stringify({ disable }),
})
